import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import moment from 'moment';
import * as Yup from 'yup';
import { Formik } from 'formik';
import Button from '../../form/Button';
import DatePicker from '../../form/DatePicker';
import { getFilenameFromUrl, objectToFormData } from 'utils/helpers';
import { editEvent, showSuccess, throwError } from 'store/slices';
import FileUpload from 'components/form/FileUpload';
import usePermission from 'hooks/usePermission';

const ImportantDates = ({
  eventId,
  fetchEventDetails,
  eventTab = false,
  handleNext,
}) => {
  const dispatch = useDispatch();
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission('Event Management');
  const { eventDates, eventData } = useSelector((state) => ({
    eventDates: state.global.eventData?.dates || {},
    eventData: state.global.eventData || {},
  }));


  const [isDataSaved, setIsDataSaved] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const initialValues = {
    early_bird_registration: eventDates?.early_bird_registration || '',
    abstract_submission: eventDates?.abstract_submission || '',
    full_paper_submission: eventDates?.full_paper_submission || '',
    registration_deadline: eventDates?.registration_deadline || '',
    brochure_image: eventData?.brochure_path || '',
    brochureFileName: eventData?.brochure_path || '',
    poster_image: eventData?.poster_path || '',
    posterFileName: eventData?.poster_path || '',
  };


  const validationSchema = Yup.object().shape({
    early_bird_registration: Yup.string().required(
      'Early bird registration date is required.'
    ),
    abstract_submission: Yup.string().required(
      'Abstract submission date is required.'
    ),
    full_paper_submission: Yup.string().required(
      'Full paper submission date is required.'
    ),
    registration_deadline: Yup.string().required(
      'Registration deadline date is required.'
    ),
    // brochure_image: Yup.string().required('Brochure image is required.'),
    // poster_image: Yup.string().required('Poster image is required.'),
  });


  const handelSave = async (values, shouldProceedNext = false) => {
    setBtnLoading(true);
    setIsDataSaved(true);
    const payloadData = { ...values, type: 1, id: eventId };
    const payload = objectToFormData(payloadData);
    const response = await dispatch(editEvent(payload));
    if (response?.status === 200) {
      fetchEventDetails();
      const text = eventDates?.early_bird_registration ? 'update' : 'add';
      if (shouldProceedNext) {
        handleNext('important_dates');
      }
      dispatch(showSuccess(`Important dates ${text} successfully.`));
    }
    setBtnLoading(false);
  };

  return (
    <div className="cmt-20">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={handelSave}
        validationSchema={validationSchema}
      >
        {(props) => {
          const { values, errors, handleChange, handleSubmit, resetForm, setFieldValue } =
            props;
          return (
            <form>
              <div className="row">
                <div className="cmb-22">
                  <DatePicker
                    label="Early Bird Registration"
                    placeholder="Select early bird date"
                    id="early_bird_registration"
                    minDate={moment().format('YYYY-MM-DD')}
                    maxDate={eventData.start_date}
                    onChange={handleChange}
                    value={values.early_bird_registration}
                    error={errors.early_bird_registration}
                  />
                </div>

                <div className="cmb-22">
                  <DatePicker
                    label="Abstract Submission"
                    placeholder="Select abstract submission date"
                    id="abstract_submission"
                    minDate={moment().format('YYYY-MM-DD')}
                    maxDate={eventData.start_date}
                    onChange={handleChange}
                    value={values.abstract_submission}
                    error={errors.abstract_submission}
                  />
                </div>

                <div className="cmb-22">
                  <DatePicker
                    label="Full Paper Submission"
                    placeholder="Select full paper submission date"
                    id="full_paper_submission"
                    minDate={moment().format('YYYY-MM-DD')}
                    maxDate={eventData.start_date}
                    onChange={handleChange}
                    value={values.full_paper_submission}
                    error={errors.full_paper_submission}
                  />
                </div>

                <div className="cmb-22">
                  <DatePicker
                    label="Registration Deadline"
                    placeholder="Select registration deadline date"
                    id="registration_deadline"
                    minDate={moment().format('YYYY-MM-DD')}
                    maxDate={eventData.start_date}
                    onChange={handleChange}
                    value={values.registration_deadline}
                    error={errors.registration_deadline}
                  />
                </div>

                <div>
                  <div className='text-20-500-25 color-new-car'>Attachments</div>
                  <hr />
                </div>
                <div className="col-md-6 cmb-24">
                  <FileUpload
                    label="Brochure"
                    fileText={getFilenameFromUrl(values.brochureFileName)}
                    id="brochure_image"
                    onChange={(e) => {
                      const value = e.target.value;
                      const fileName = e.target.fileName;
                      let fileType = fileName
                        ? fileName?.split('.')?.pop()
                        : '';
                      if (
                        ['png', 'jpg', 'jpeg', 'pdf'].includes(fileType)
                      ) {
                        setFieldValue('brochure_image', value);
                        setFieldValue('brochureFileName', fileName);
                      } else {
                        setFieldValue('brochureFileName', '');
                        dispatch(
                          throwError({
                            message: 'Invalid file selected',
                          })
                        );
                      }
                    }}
                    value={values.brochure_image}
                    error={errors.brochure_image}
                  // disabled={isViewMode}
                  />
                </div>
                <div className="col-md-6 cmb-24">
                  <FileUpload
                    label="Poster"
                    fileType="image"
                    fileText={getFilenameFromUrl(values.posterFileName)}
                    id="poster_image"
                    onChange={(e) => {
                      const value = e.target.value;
                      const fileName = e.target.fileName;
                      let fileType = fileName
                        ? fileName?.split('.')?.pop()
                        : '';
                      if (['png', 'jpg', 'jpeg'].includes(fileType)) {
                        setFieldValue('poster_image', value);
                        setFieldValue('posterFileName', fileName);
                      } else {
                        setFieldValue('posterFileName', '');
                        dispatch(
                          throwError({
                            message: 'Invalid file selected',
                          })
                        );
                      }
                    }}
                    value={values.poster_image}
                    error={errors.poster_image}
                  // disabled={isViewMode}
                  />
                </div>


                {eventTab ? (
                  <div className="d-flex justify-content-center gap-4 cmt-20 cmb-20">
                    {(updateAccess === '1' || createAccess === '1') && (
                      <>
                        <Button
                          text="Done"
                          btnStyle="primary-dark"
                          className="cps-40 cpe-40 h-37"
                          onClick={handleSubmit}
                          btnLoading={btnLoading}
                          disabled={isEqual(values, eventDates) || btnLoading}
                        />
                        <Button
                          text="Reset"
                          btnStyle="light-outline"
                          className="cps-40 cpe-40 h-37"
                          onClick={resetForm}
                          disabled={btnLoading}
                        />

                        <Button
                          text={
                            isDataSaved || isEqual(initialValues, values)
                              ? 'Proceed Next'
                              : 'Proceed Next & Save'
                          }
                          btnStyle="light-outline"
                          className="h-37 cps-20 cpe-20"
                          onClick={() => {
                            if (isDataSaved || isEqual(initialValues, values)) {
                              handleNext('important_dates');
                            } else {
                              handelSave(values, true);
                            }
                          }}
                          disabled={btnLoading}
                        />
                      </>
                    )}
                  </div>
                ) : (
                  <div className="d-flex justify-content-center gap-4 cmt-20">
                    <Button
                      text="Cancel"
                      isRounded
                      btnStyle="light-outline"
                      className="cps-40 cpe-40"
                      onClick={resetForm}
                    />
                    <Button
                      text="Done"
                      isRounded
                      btnStyle="primary-dark"
                      className="cps-40 cpe-40"
                      onClick={handleSubmit}
                      btnLoading={btnLoading}
                      disabled={isEqual(values, eventDates)}
                    />
                  </div>
                )}
              </div>


            </form>
          );
        }}
      </Formik>
    </div>
  );
};
export default ImportantDates;
